import {useEffect, useRef} from 'react';
import {Animated, Platform} from 'react-native';

/**
 * Header state motion (spec § 7.2): rows and controls move with `translateY` / `translateX`
 * and fade with `opacity`. No layout property animates, so the header box keeps its full
 * height. RN `Animated`, not Reanimated: Reanimated 4.5 crashes in updateProps on web.
 *
 * SHORTCUT: 200 ms is a prototype choice; no motion token exists. Upgrade path: a DIS-1735
 * motion token, or the duration design gives for the compact transition.
 */
export const COMPACT_TRANSITION_MS = 200;

/**
 * 1 while `on`, 0 otherwise. The first render takes the value without animating; `instant`
 * (reduced motion) takes every change without animating.
 */
export const useProgress = (on: boolean, instant = false): Animated.Value => {
  const value = useRef(new Animated.Value(on ? 1 : 0)).current;
  useEffect(() => {
    if (instant) {
      value.setValue(on ? 1 : 0);
      return;
    }
    Animated.timing(value, {
      toValue: on ? 1 : 0,
      duration: COMPACT_TRANSITION_MS,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [on, instant, value]);
  return value;
};

/**
 * Props that take a faded-out element and its descendants out of pointer, keyboard and
 * screen-reader reach. `aria-hidden` does not remove focusable descendants from the tab
 * order; `inert` does. react-native-web 0.21 forwards `inert` to the DOM (`<div inert="">`,
 * measured with react-dom/server 19.2); native ignores it.
 */
export const hiddenWhen = (hidden: boolean) =>
  hidden
    ? ({
        pointerEvents: 'none',
        'aria-hidden': true,
        importantForAccessibility: 'no-hide-descendants',
        inert: true,
      } as const)
    : ({pointerEvents: 'auto'} as const);
